/**
 * ReportCallModal - modal for reporting a problem with a grant call.
 *
 * Features:
 * - Required description field (min 1 char after trim, max 1000)
 * - Character counter with color change at >900 (amber) and >980 (red)
 * - State reset when modal is opened (prevents state leak between different calls)
 * - Accessibility: focus trap (useFocusTrap), role=dialog, aria-modal, aria-labelledby, ESC close, focus return
 * - POST /grants/calls/:id/report with success/error/429 toasts
 *
 * @see docs/specifications/2026-06-01-grants-calls-report-problem/spec.md
 */
import React, { useState, useEffect, useRef } from "react";
import { useToast } from "../../../contexts/ToastContext";
import { useFocusTrap } from "../../../hooks/useFocusTrap";
import apiClient from "../../../services/apiClient";

interface ReportCallModalProps {
  /** UUID of the grant call being reported */
  callId: string;
  /** Title of the grant call (display only) */
  callTitle: string;
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
}

const MODAL_TITLE_ID = "report-call-modal-title";
const CALL_TITLE_ID = "report-call-title";

const ReportCallModal: React.FC<ReportCallModalProps> = ({
  callId,
  callTitle,
  isOpen,
  onClose,
}) => {
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addToast } = useToast();
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus trap for accessibility (also restores focus to trigger on cleanup)
  useFocusTrap(dialogRef, isOpen);

  // Reset reason when modal opens/closes (prevents state leak between different calls)
  useEffect(() => {
    setReason("");
  }, [isOpen]);

  // Focus the dialog when opened
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isOpen]);

  // Handle ESC key (useFocusTrap does not handle Escape)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (reason.trim().length === 0) return;

    setIsSubmitting(true);
    try {
      await apiClient.post(`/grants/calls/${callId}/report`, { reason });
      addToast({
        type: "success",
        message: "Dziękujemy za zgłoszenie! Zajmiemy się tym problemem.",
      });
      handleClose();
    } catch (error: unknown) {
      const status =
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "status" in error.response
          ? (error.response as { status: number }).status
          : undefined;

      if (status === 429) {
        addToast({
          type: "warning",
          message:
            "Zbyt częste zgłaszanie. Odczekaj chwilę przed kolejnym zgłoszeniem.",
        });
      } else {
        addToast({
          type: "error",
          message: "Wystąpił błąd podczas wysyłania zgłoszenia. Spróbuj ponownie.",
        });
      }
      // Modal remains open on error (do NOT call onClose)
    } finally {
      setIsSubmitting(false);
    }
  };

  const charCount = reason.length;
  const counterClass =
    charCount > 980
      ? "text-red-600"
      : charCount > 900
      ? "text-amber-600"
      : "text-neutral-500";

  const isSubmitDisabled = reason.trim().length === 0 || isSubmitting;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
      onClick={handleClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={MODAL_TITLE_ID}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 focus:outline-none"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 16px)" }}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <h2
              id={MODAL_TITLE_ID}
              className="text-xl font-semibold text-neutral-900"
            >
              Zgłoś problem z naborem
            </h2>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-neutral-400 hover:text-neutral-600 transition-colors ml-4 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
              aria-label="Zamknij modal"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Call title (read-only) */}
          <div className="mb-4">
            <p className="text-sm text-neutral-500 mb-1">Nabór:</p>
            <p
              id={CALL_TITLE_ID}
              className="text-sm font-medium text-neutral-900 line-clamp-2"
            >
              {callTitle}
            </p>
          </div>

          <form onSubmit={handleSubmit} aria-describedby={CALL_TITLE_ID}>
            <div className="mb-4">
              <label
                htmlFor="report-call-reason"
                className="block text-sm font-medium text-neutral-700 mb-2"
              >
                Opis problemu{" "}
                <span className="text-red-600" aria-hidden="true">
                  *
                </span>
              </label>
              <textarea
                id="report-call-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={1000}
                rows={4}
                disabled={isSubmitting}
                aria-required="true"
                placeholder="Opisz problem z tym naborem..."
                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed resize-none"
              />
              <div className="flex justify-end mt-1">
                <span className={`text-xs ${counterClass}`}>
                  {charCount}/1000
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anuluj
              </button>
              <button
                type="submit"
                disabled={isSubmitDisabled}
                aria-busy={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Wysyłanie..." : "Wyślij zgłoszenie"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReportCallModal;
